/* Phase 0 — Foundation page definitions. Each page is a thin descriptor
   passed to <ResourcePage/>, keeping every screen consistent. */
import ResourcePage from "@/components/foundation/ResourcePage";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import BulkImportDialog from "@/components/BulkImportDialog";

const codeNameFields = [
  { key: "code", label: "Code" },
  { key: "name", label: "Name", wide: true },
  { key: "description", label: "Description", type: "textarea", wide: true },
];

export function Organizations() {
  return <ResourcePage
    title="Organizations"
    subtitle="The legal/operational parent entities under your tenant. A tenant may host multiple organizations across geographies."
    endpoint="/foundation/organizations" queryKey="organizations"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "country", label: "Country" }, { key: "currency", label: "Currency" },
      { key: "is_active", label: "Active", type: "bool", defaultValue: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "country", label: "Country" },
      { key: "currency", label: "Currency" },
      { key: "is_active", label: "Status", render: (r) => r.is_active === false ? <Badge variant="outline">Disabled</Badge> : <Badge variant="outline" className="text-primary border-primary/40">Active</Badge> },
    ]}
  />;
}

export function Companies() {
  return <ResourcePage
    title="Companies"
    subtitle="Legal company master under each organization. Select the parent Organization, then add GST/PAN/CIN and fiscal calendar."
    endpoint="/foundation/companies" queryKey="companies"
    fields={[
      { key: "organization_id", label: "Organization", type: "select", wide: true, required: true,
        optionsEndpoint: "/foundation/organizations", optionLabel: "name", optionValue: "id", nameKey: "organization_name" },
      { key: "code", label: "Code" }, { key: "name", label: "Legal name", wide: true },
      { key: "gstin", label: "GSTIN" }, { key: "pan", label: "PAN" }, { key: "cin", label: "CIN" },
      { key: "country", label: "Country" }, { key: "currency", label: "Currency" },
      { key: "fiscal_year_start", label: "FY start (MM-DD)" },
      { key: "address", label: "Registered address", type: "textarea", wide: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Legal name" },
      { key: "organization_name", label: "Organization", render: (r) => r.organization_name || "—" },
      { key: "gstin", label: "GSTIN", render: (r) => <span className="font-mono text-xs">{r.gstin || "—"}</span> },
      { key: "country", label: "Country" },
    ]}
  />;
}

export function Branches() {
  return <ResourcePage
    title="Branches"
    subtitle="Branches under each company. Select the parent Company — Stores are then created under a Branch."
    endpoint="/foundation/branches" queryKey="branches"
    fields={[
      { key: "company_id", label: "Company", type: "select", wide: true, required: true,
        optionsEndpoint: "/foundation/companies", optionLabel: "name", optionValue: "id", nameKey: "company_name" },
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "type", label: "Type (branch/region/office)" }, { key: "city", label: "City" },
      { key: "state", label: "State" }, { key: "country", label: "Country" },
      { key: "address", label: "Address", type: "textarea", wide: true },
      { key: "is_active", label: "Active", type: "bool", defaultValue: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "company_name", label: "Company", render: (r) => r.company_name || "—" },
      { key: "city", label: "City" },
      { key: "country", label: "Country" },
    ]}
  />;
}

export function Stores() {
  const qcStores = useQueryClient();
  const { data: health } = useQuery({
    queryKey: ["pos", "terminal-health"],
    queryFn: async () => (await api.get("/pos/terminal-health")).data,
    refetchInterval: 30_000,
  });
  const hmap = health || {};
  const renderHealth = (r) => {
    const h = hmap[r.id] || hmap[r._id];
    if (!h || !h.total) return <span className="text-xs text-muted-foreground">No terminals</span>;
    return (
      <div className="flex items-center gap-1.5" data-testid={`store-health-${r.id}`}>
        <Badge variant="outline" className="text-[10px]">{h.total} total</Badge>
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{h.online} online</span>
        {h.offline > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-rose-600"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" />{h.offline} offline</span>}
      </div>
    );
  };
  return <ResourcePage
    title="Stores"
    subtitle="The single store master. Select the parent Branch. Stores here are shared with Store Commerce → POS Administration, where terminals & salespersons are assigned."
    endpoint="/foundation/stores" queryKey="foundation-stores"
    canDelete={false}
    headerActions={<BulkImportDialog entityLabel="Store" triggerLabel="Bulk import" testId="store-bulk"
      templateUrl="/pos/stores/import-template" previewUrl="/pos/stores/import-preview" importUrl="/pos/stores/bulk-import"
      onImported={() => qcStores.invalidateQueries({ queryKey: ["foundation-stores"] })} />}
    fields={[
      { key: "branch_id", label: "Branch", type: "select", wide: true, required: true,
        optionsEndpoint: "/foundation/branches", optionLabel: "name", optionValue: "id", nameKey: "branch_name" },
      { key: "code", label: "Store code" }, { key: "name", label: "Store name", wide: true },
      { key: "store_format", label: "Format (e.g. Supermarket)", defaultValue: "Supermarket" },
      { key: "store_type", label: "Type (e.g. Company Owned)", defaultValue: "Company Owned" },
      { key: "city", label: "City" }, { key: "state_code", label: "State code", defaultValue: "MH" },
      { key: "gstin", label: "GSTIN" },
      { key: "status", label: "Active", type: "bool", defaultValue: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code || "—"}</span> },
      { key: "name", label: "Store" },
      { key: "branch_name", label: "Branch", render: (r) => r.branch_name || "—" },
      { key: "store_format", label: "Format", render: (r) => <Badge variant="outline" className="text-[10px]">{r.store_format || "—"}</Badge> },
      { key: "terminals", label: "Terminal health", render: renderHealth },
      { key: "city", label: "City" },
    ]}
  />;
}

export function Currencies() {
  return <ResourcePage
    title="Currencies"
    subtitle="Global currency master. Used by POS, OMS and Finance. Superadmin can add new currencies."
    endpoint="/foundation/currencies" queryKey="currencies"
    fields={[
      { key: "code", label: "ISO Code" }, { key: "name", label: "Name", wide: true },
      { key: "symbol", label: "Symbol" }, { key: "decimals", label: "Decimals", type: "number", defaultValue: 2 },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "symbol", label: "Symbol", render: (r) => <span className="font-mono text-lg">{r.symbol}</span> },
      { key: "decimals", label: "Decimals" },
    ]}
  />;
}

export function ExchangeRates() {
  return <ResourcePage
    title="Exchange rates"
    subtitle="Daily FX rates against base currency. Used by multi-currency POS and finance."
    endpoint="/foundation/exchange-rates" queryKey="exchange_rates"
    fields={[
      { key: "from_currency", label: "From" }, { key: "to_currency", label: "To" },
      { key: "rate", label: "Rate", type: "number" },
      { key: "as_of", label: "As of (YYYY-MM-DD)" },
    ]}
    columns={[
      { key: "from_currency", label: "From" },
      { key: "to_currency", label: "To" },
      { key: "rate", label: "Rate", render: (r) => <span className="font-mono">{r.rate}</span> },
      { key: "as_of", label: "As of" },
    ]}
  />;
}

export function Languages() {
  return <ResourcePage
    title="Languages"
    subtitle="Supported UI languages. India: English, Hindi, Bengali, Tamil, Telugu, Kannada, Malayalam, Marathi, Gujarati, Punjabi. Global: Arabic (RTL), French, German, Spanish, Japanese, Chinese."
    endpoint="/foundation/languages" queryKey="languages"
    fields={[
      { key: "code", label: "ISO Code" }, { key: "name", label: "Name" },
      { key: "native_name", label: "Native name" },
      { key: "is_rtl", label: "Right-to-left", type: "bool" },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "native_name", label: "Native", render: (r) => <span className="font-display">{r.native_name}</span> },
      { key: "is_rtl", label: "RTL", render: (r) => r.is_rtl ? <Badge variant="outline">RTL</Badge> : "—" },
    ]}
  />;
}

export function Timezones() {
  return <ResourcePage
    title="Time zones"
    subtitle="All stores, transactions and audit events are stored in UTC and rendered in the user's selected time zone."
    endpoint="/foundation/timezones" queryKey="timezones"
    fields={[
      { key: "code", label: "IANA ID" }, { key: "name", label: "Name", wide: true },
      { key: "utc_offset", label: "UTC offset" },
    ]}
    columns={[
      { key: "code", label: "IANA ID", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "utc_offset", label: "Offset", render: (r) => <span className="font-mono">{r.utc_offset}</span> },
    ]}
  />;
}

export function Countries() {
  return <ResourcePage
    title="Countries"
    subtitle="Country master with default currency and time zone."
    endpoint="/foundation/countries" queryKey="countries"
    fields={[
      { key: "code", label: "ISO Code" }, { key: "name", label: "Name" },
      { key: "default_currency", label: "Default currency" },
      { key: "default_timezone", label: "Default timezone" },
      { key: "flag", label: "Flag" },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
      { key: "flag", label: "", render: (r) => <span className="text-lg">{r.flag || ""}</span> },
      { key: "name", label: "Name" },
      { key: "default_currency", label: "Currency" },
      { key: "default_timezone", label: "Time zone", render: (r) => <span className="font-mono text-xs">{r.default_timezone}</span> },
    ]}
  />;
}

export function SubscriptionPlans() {
  return <ResourcePage
    title="Subscription plans"
    subtitle="SaaS plan catalog. Tenants subscribe to one of these plans; each tier maps to feature flags and quota limits."
    endpoint="/foundation/subscription-plans" queryKey="subscription_plans"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "description", label: "Description", type: "textarea", wide: true },
      { key: "price_inr", label: "Price (INR / month)", type: "number" },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <Badge variant="outline" className="font-mono text-[10px] uppercase">{r.code}</Badge> },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
      { key: "price_inr", label: "Price", render: (r) => r.price_inr ? `₹${(r.price_inr).toLocaleString("en-IN")}/mo` : "Contact sales" },
    ]}
  />;
}

export function FeatureFlags() {
  return <ResourcePage
    title="Feature flags"
    subtitle="Toggle modules on/off per tenant. Global defaults exist for every flag and are inherited unless overridden."
    endpoint="/foundation/feature-flags" queryKey="feature_flags"
    fields={[
      { key: "key", label: "Key" }, { key: "name", label: "Name", wide: true },
      { key: "enabled", label: "Enabled", type: "bool" },
      { key: "rollout", label: "Rollout %", type: "number", defaultValue: 100 },
    ]}
    columns={[
      { key: "key", label: "Key", render: (r) => <span className="font-mono text-xs">{r.key}</span> },
      { key: "name", label: "Name" },
      { key: "enabled", label: "Status", render: (r) => r.enabled ? <Badge variant="outline" className="text-primary border-primary/40">Enabled</Badge> : <Badge variant="outline">Disabled</Badge> },
      { key: "rollout", label: "Rollout", render: (r) => `${r.rollout ?? 100}%` },
    ]}
  />;
}

export function Configurations() {
  return <ResourcePage
    title="Configuration"
    subtitle="Global and tenant-scoped key/value configuration. Any module can read configuration via this central store."
    endpoint="/foundation/configurations" queryKey="configurations"
    fields={[
      { key: "key", label: "Key" }, { key: "value", label: "Value", wide: true },
      { key: "category", label: "Category" }, { key: "description", label: "Description", type: "textarea", wide: true },
    ]}
    columns={[
      { key: "key", label: "Key", render: (r) => <span className="font-mono text-xs">{r.key}</span> },
      { key: "value", label: "Value" },
      { key: "category", label: "Category" },
    ]}
  />;
}

export function NumberSeries() {
  return <ResourcePage
    title="Number series"
    subtitle="Auto-incrementing numbering used by Orders, Invoices, POs, GRNs, Customers, Vendors etc."
    endpoint="/foundation/number-series" queryKey="number_series"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "prefix", label: "Prefix" }, { key: "padding", label: "Padding (digits)", type: "number", defaultValue: 6 },
      { key: "suffix", label: "Suffix" }, { key: "current", label: "Current", type: "number" },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <Badge variant="outline" className="font-mono text-[10px]">{r.code}</Badge> },
      { key: "name", label: "Name" },
      { key: "prefix", label: "Format", render: (r) => <span className="font-mono text-xs">{r.prefix}{"0".repeat(r.padding || 6)}{r.suffix || ""}</span> },
      { key: "current", label: "Current #", render: (r) => <span className="font-mono">{r.current}</span> },
    ]}
  />;
}

export function NotificationTemplates() {
  return <ResourcePage
    title="Notification templates"
    subtitle="Reusable templates with handlebars-style variables. Supports email, SMS, WhatsApp, push, in-app and webhook."
    endpoint="/foundation/notification-templates" queryKey="notification_templates"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "channel", label: "Channel (in_app/email/sms/whatsapp/push/webhook)" },
      { key: "subject", label: "Subject" },
      { key: "body", label: "Body", type: "textarea", wide: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name" },
      { key: "channel", label: "Channel", render: (r) => <Badge variant="outline" className="text-[10px] uppercase">{r.channel}</Badge> },
    ]}
  />;
}

export function Documents() {
  return <ResourcePage
    title="Documents"
    subtitle="Document repository with versioning, retention, metadata and tags."
    endpoint="/foundation/documents" queryKey="documents"
    fields={[
      { key: "title", label: "Title", wide: true }, { key: "category", label: "Category" },
      { key: "url", label: "URL" }, { key: "version", label: "Version" }, { key: "retention_days", label: "Retention (days)", type: "number" },
      { key: "tags", label: "Tags (comma-separated)", wide: true },
    ]}
    columns={[
      { key: "title", label: "Title" },
      { key: "category", label: "Category" },
      { key: "version", label: "Ver" },
      { key: "created_at", label: "Created", render: (r) => r.created_at?.slice(0, 10) },
    ]}
  />;
}

export function AIModels() {
  return <ResourcePage
    title="AI models registry"
    subtitle="Catalog of supported models per provider. The DecisionOS chat picks from this registry."
    endpoint="/foundation/ai-models" queryKey="ai_models"
    fields={[
      { key: "provider", label: "Provider (openai/anthropic/gemini/azure/ollama)" },
      { key: "model_id", label: "Model id", wide: true },
      { key: "display_name", label: "Display name" },
      { key: "context_window", label: "Context window", type: "number" },
      { key: "input_cost_1k", label: "Input cost / 1k tokens", type: "number" },
      { key: "output_cost_1k", label: "Output cost / 1k tokens", type: "number" },
    ]}
    columns={[
      { key: "provider", label: "Provider", render: (r) => <Badge variant="outline" className="capitalize text-[10px]">{r.provider}</Badge> },
      { key: "model_id", label: "Model", render: (r) => <span className="font-mono text-xs">{r.model_id}</span> },
      { key: "display_name", label: "Name" },
      { key: "context_window", label: "Context" },
    ]}
  />;
}

export function Prompts() {
  return <ResourcePage
    title="Prompt registry"
    subtitle="Reusable system prompts used by DecisionOS and agents."
    endpoint="/foundation/prompts" queryKey="prompts"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "description", label: "Description", type: "textarea", wide: true },
      { key: "body", label: "Prompt body", type: "textarea", wide: true },
    ]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name" },
    ]}
  />;
}

export function ApiRegistry() {
  return <ResourcePage
    title="API registry"
    subtitle="Catalog of registered APIs with version, owner, throttle and SLA metadata."
    endpoint="/foundation/api-registry" queryKey="api_registry"
    fields={[
      { key: "code", label: "Code" }, { key: "name", label: "Name", wide: true },
      { key: "method", label: "Method" }, { key: "path", label: "Path" },
      { key: "version", label: "Version" }, { key: "owner", label: "Owner team" },
      { key: "rate_limit", label: "Rate limit / min", type: "number" },
    ]}
    columns={[
      { key: "method", label: "Method", render: (r) => <Badge variant="outline" className="font-mono text-[10px]">{r.method}</Badge> },
      { key: "path", label: "Path", render: (r) => <span className="font-mono text-xs">{r.path}</span> },
      { key: "version", label: "Ver" },
      { key: "owner", label: "Owner" },
    ]}
  />;
}
